import i18n, { type BackendModule } from "i18next"
import { initReactI18next } from "react-i18next"

import translationDEUrl from "./locales/de.json?url"
import translationEN from "./locales/en.json"

// English ships in the app file; German is a plain JSON asset fetched the first
// time it is switched to. A plain fetch, not `import()`: a failed module import
// goes through Vite's preload handler, which reloads the app, while a failed
// fetch only leaves the screen in English. i18next keeps the current language
// on screen until the file is in, so nobody sees raw keys.
export const languageFiles: Record<string, string> = {
  de: translationDEUrl
}

export const LANGUAGE_STORAGE_KEY = "ezbuild.language"

// How long the first screen waits for the language this browser used last;
// after that it renders in English and switches when the file arrives.
export const FIRST_PAINT_LANGUAGE_WAIT_MS = 2500

const readStoredLanguage = (): string | null => {
  try {
    const value = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    return value && value in languageFiles ? value : null
  } catch {
    return null
  }
}

const storeLanguage = (language: string) => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  } catch {
    // Blocked storage: the next visit's first screen is English.
  }
}

const languageFileBackend: BackendModule = {
  type: "backend",
  init: () => {},
  read: (language, _namespace, callback) => {
    const url = languageFiles[language]
    if (!url) {
      callback(null, {})
      return
    }
    fetch(url, { credentials: "same-origin" })
      .then((response) => {
        if (!response.ok) throw new Error(`${url}: ${response.status}`)
        return response.json()
      })
      .then(
        (translations) => callback(null, translations),
        // `true` lets i18next retry a few times with backoff before it gives
        // up and the keys fall back to English.
        (error: Error) => callback(error, true)
      )
  }
}

// Read before init, whose own switch to English is stored like any other.
const storedLanguage = readStoredLanguage()

i18n
  .use(languageFileBackend)
  // pass the i18n instance to react-i18next.
  .use(initReactI18next)
  // for all options read: https://www.i18next.com/overview/configuration-options
  .init({
    resources: {
      en: {
        translation: translationEN
      }
    },
    // Keep the bundled English and ask the backend only for what is missing.
    partialBundledLanguages: true,
    lng: "en",
    supportedLngs: ["en", "de"],
    fallbackLng: "en",
    debug: false,

    interpolation: {
      escapeValue: false // not needed for react as it escapes by default
    }
  })

// i18next 21 applies every switch when its file arrives, even one a later
// switch replaced, so a slow German file could land over a newer English.
let requestedLanguage = i18n.language
i18n.on("languageChanging", (language: string) => {
  requestedLanguage = language
})
i18n.on("languageChanged", (language: string) => {
  if (language !== requestedLanguage) {
    void i18n.changeLanguage(requestedLanguage)
    return
  }
  storeLanguage(language)
})

/**
 * Starts the language this browser used last and resolves when it is on
 * screen or after `waitMs`, whichever is first; never rejects.
 */
export const startStoredLanguage = (
  waitMs = FIRST_PAINT_LANGUAGE_WAIT_MS
): Promise<void> => {
  if (!storedLanguage) return Promise.resolve()
  const switched = i18n.changeLanguage(storedLanguage).then(
    () => undefined,
    () => undefined
  )
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, waitMs))
  return Promise.race([switched, timeout])
}

export default i18n
